import { useFormContext } from 'react-hook-form'
import { DEFAULT_ADDITIONAL_OBJECTS_LIMIT, IntegrationServiceId } from '../../../../../../../common/integrations'
import { IntegrationSettings } from '../../../schema'
import { useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { DetailedCRMAccountIntegrationMapping, MappingType } from '../../../../../../../models/Account'
import { AdditionalObjectsBody } from './AdditionalObjectsBody'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { ObjectRowShell } from '../shared/ObjectRowShell'

interface AdditionalObjectRowProps {
  serviceId: IntegrationServiceId
  index: number
  excludeObjectNames?: Set<string>
}

export const AdditionalObjectRow: React.FC<AdditionalObjectRowProps> = ({ serviceId, index, excludeObjectNames }) => {
  const { setValue, watch } = useFormContext<IntegrationSettings>()

  const [activeKey, setActiveKey] = useState<string | null>(null)

  const accountId = useAppSelector(selectAccountId)
  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )
  const additionalObjects = watch('mappings.additionalObjects') as DetailedCRMAccountIntegrationMapping[]
  const contactObjectName = watch('mappings.contact.objectName')

  const objectName = useMemo(() => additionalObjects[index]?.objectName, [additionalObjects, index])

  const isDisabled: boolean = useMemo(
    () => !!(additionalObjects[index]?.additionalInformation?.isSessionObject || additionalObjects[index]?.additionalInformation?.isTaskObject),
    [additionalObjects, index],
  )

  const objectsLimit = useMemo(() => additionalObjects[index]?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT, [additionalObjects, index])

  const options = useMemo(() => {
    return supportedObjects?.filter((obj) => obj.name !== contactObjectName).map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects, contactObjectName])

  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    setValue(
      `mappings.additionalObjects.${index}`,
      { objectName: option.id, mappingType: MappingType.CUSTOM, limit: objectsLimit },
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const handleDelete = () => {
    setActiveKey(null)

    const currentList = additionalObjects || []
    const updatedList = currentList.filter((_, i) => i !== index)
    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const handleDuplicate = () => {
    const currentList = additionalObjects || []

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isSessionObject, isTaskObject, ...rest } = currentList[index]?.additionalInformation ?? {}

    const itemToClone: DetailedCRMAccountIntegrationMapping = {
      ...currentList[index],
      additionalInformation: rest,
    }

    const updatedList = [...currentList]
    updatedList.splice(index + 1, 0, { ...itemToClone })

    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const onLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const numericValue = value === '' ? 0 : parseInt(value, 10)
    const currentList = additionalObjects || []
    const updatedObject = { ...currentList[index], limit: numericValue }
    const updatedList = [...currentList]
    updatedList[index] = updatedObject

    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const handleAccordionSelect = (eventKey: string | null | undefined) => {
    if (eventKey === String(index)) {
      setActiveKey(String(index))
    } else {
      setActiveKey(null)
    }
  }

  return (
    <ObjectRowShell
      index={index}
      activeKey={activeKey}
      onAccordionSelect={handleAccordionSelect}
      options={options}
      excludeObjectNames={excludeObjectNames}
      selectedObjectName={objectName}
      onOptionSelected={handleCustomOptionSelect}
      loading={isLoadingSupportedObjects}
      disabled={isDisabled}
      menuItems={[
        { label: 'Duplicate', icon: 'bi-files', onClick: handleDuplicate },
        { label: 'Delete', icon: 'bi-trash', onClick: handleDelete, disabled: isDisabled, danger: true },
      ]}
      headerExtra={
        <div onClick={(e) => e.stopPropagation()} className='ms-2 me-3 pe-auto'>
          <Form.Control
            type='text'
            defaultValue={objectsLimit}
            className='text-center px-1'
            style={{ width: '60px' }}
            maxLength={3}
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault()
              } else if (e.key === '0' && e.currentTarget.value === '') {
                e.preventDefault()
              }
            }}
            onChange={onLimitChange}
          />
        </div>
      }
      body={<AdditionalObjectsBody serviceId={serviceId} additionalObjectIndex={index} isDisabled={isDisabled} />}
    />
  )
}
